/**
 * The messages panel's behavior half: the tab's chat (`useWebChat`) under the chosen filter, as
 * days of messages ready to draw, and the panel's actions. Draws nothing.
 *
 * - Opened at a message (a banner's click), the panel shows All, so that message is in the stream.
 * - A notice's door is the website page `webDoorHref` maps it to; a door with no website page
 *   (an invite, a consumed form) shows no action, as in the Mini App.
 * - Every message carries its room: the client's and the studio's secure messages the lock, every
 *   message from the Telegram chat, notices included, the Telegram mark (design D2).
 * - The stream is the phone's (`useWebChatThread`) under the rest gate the panel's scroll shares
 *   (useChatThreadScroll): while the reader is scrolled up and moving, what arrives waits for rest.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ChatThreadGate } from '@/hooks/chat/useChatThreadScroll';
import {
  browserTimezone,
  CHAT_FILTER_LABELS,
  clientDelivery,
  clientDoorLabel,
  clientMediaLine,
  clientStateLine,
  formatClientTime,
  groupByClientDay,
} from '@/lib/client/clientChatThread';

import { useSiteStudio } from '../../api/site';
import { useWebSession } from '../../api/session';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import type { SendIssue } from '../../messages/chatHub';
import { webDoorHref } from '../../messages/messageDoors';
import { WEB_MESSAGE_FILTERS, type WebMessageFilter } from '../../messages/messageFilters';
import type { StreamItem } from '../../messages/messageStream';
import { useMessagesPanel } from '../../messages/panelContext';
import { useWebChatThread } from '../../messages/useWebChatThread';
import { useWebChat } from '../../messages/WebChat';
import { usePageMover } from '../../pageMover';
import {
  contract,
  type MessagesDay,
  type MessagesPanelActions,
  type MessagesPanelEvents,
  type MessagesPanelState,
  type PanelMessage,
} from './contract';

export type MessagesPanelRuntime = PieceRuntime<typeof contract, MessagesPanelState, MessagesPanelActions>;

function messageOf({ room, entry }: StreamItem, timezone: string, issues: ReadonlyMap<string, SendIssue>): PanelMessage {
  const base = {
    id: entry.id,
    room,
    from: entry.direction === 'inbound' ? ('you' as const) : ('studio' as const),
    time: formatClientTime(entry.at, timezone),
    html: null,
    delivery: null,
    door: null,
    state: null,
    stateStatus: null,
    preparation: [],
    preparationLabel: null,
  };
  switch (entry.kind) {
    case 'chat-media':
      return { ...base, kind: 'media', text: clientMediaLine(entry.payload.mediaType) };
    case 'reply':
      return { ...base, kind: 'message', text: entry.payload.text };
    case 'chat-message':
      return { ...base, kind: 'message', text: entry.payload.text, delivery: clientDelivery(entry, room === 'secure', issues.get(entry.id) ?? null) };
    case 'automatic': {
      const { payload } = entry;
      const href = webDoorHref(payload.clientDoor);
      return {
        ...base,
        kind: 'notice',
        text: payload.label,
        html: payload.text,
        door: href && payload.clientDoor ? { label: clientDoorLabel(payload.clientDoor), href } : null,
        state: payload.state ? clientStateLine(payload.state, timezone) : null,
        stateStatus: payload.state?.status ?? null,
        preparation: (payload.preparation ?? []).map((page) => ({ id: page.id, html: page.text })),
        preparationLabel: payload.preparation?.length ? payload.preparationLabel || 'How to prepare' : null,
      };
    }
  }
}

/** The stream by day in the client's own time zone. */
function daysOf(items: readonly StreamItem[], timezone: string, issues: ReadonlyMap<string, SendIssue>): MessagesDay[] {
  return groupByClientDay(items, (item) => item.entry.at, timezone).map(({ key, label, items: day }) => ({
    key,
    label,
    messages: day.map((item) => messageOf(item, timezone, issues)),
  }));
}

export function useMessagesPanelPiece(gate: ChatThreadGate, input: PieceInput<MessagesPanelEvents> = {}): MessagesPanelRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const panel = useMessagesPanel();
  const { hub, state: chat } = useWebChat();
  const studio = useSiteStudio().data;
  const session = useWebSession();
  const [filter, setFilter] = useState<WebMessageFilter>('all');
  const timezone = useMemo(() => browserTimezone(), []);

  // Opened at a message: All shows it.
  useEffect(() => {
    if (panel.open && panel.atEntryId) setFilter('all');
  }, [panel.open, panel.atEntryId]);

  const thread = useWebChatThread(filter, gate);
  const { entries, secureOf } = thread;
  const days = useMemo(
    () => daysOf(entries.map((entry): StreamItem => ({ room: secureOf(entry) ? 'secure' : 'telegram', entry })), timezone, chat.issues),
    [entries, secureOf, timezone, chat.issues],
  );

  const { closeMessages } = panel;
  const actions = useMemo<MessagesPanelActions>(
    () => ({
      close: () => {
        closeMessages();
        emit('closed', {});
      },
      chooseFilter: (id) => setFilter(id),
      loadOlder: thread.loadOlder,
      send: (text) => {
        hub.send(text);
        emit('sent', {});
      },
      retrySend: (id) => hub.retrySend(id),
      typing: () => hub.notifyTyping(),
      openDoor: (href) => {
        closeMessages();
        emit('doorOpened', { href });
        navigate(href);
      },
      retry: () => hub.retryLoad(),
    }),
    [closeMessages, emit, hub, navigate, thread.loadOlder],
  );

  return {
    state: {
      open: panel.open,
      studioName: studio?.name ?? '',
      logoUrl: studio?.logoUrl ?? null,
      filters: WEB_MESSAGE_FILTERS.map((id) => ({ id, label: CHAT_FILTER_LABELS[id], current: id === filter })),
      status: thread.status,
      days,
      hasOlder: thread.hasOlder,
      loadingOlder: chat.loadingOlder,
      // The studio's typing shows only under the filters holding its secure messages (row M12).
      typing: thread.typing,
      focusId: panel.open ? panel.atEntryId : null,
      draftScope: `web:${session.user?.id ?? ''}`,
    },
    actions,
    options,
  };
}
